import { useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { useGetOrganization } from "~/api/organizations/organizations";
import { MarkdownContentModal } from "~/components/MarkdownContentModal";
import { useParentCase } from "~/features/case/hooks/useParentCase";
import { formatCaseInfo } from "~/features/chat/helpers";
import { useChatDocumentParams } from "~/helpers";
import { trackEvent } from "~/utils/analytics";
import {
  highlightTermsInMarkdown,
  unwrapAllCodeBlocks,
} from "~/utils/markdownTerms";
import { getFileTitle } from "../../helpers";
import { GenerateReportView } from "./GenerateReportView";
import { RegenerateReportModal } from "./RegenerateReportModal";
import { ReportActions } from "./ReportActions";
import { ReportView, ReportViewStatus } from "./ReportView";
import {
  OutdatedReason,
  TemplateReportItem,
  TemplateReportSelector,
  TemplateReportStatus,
} from "./TemplateReportSelector";
import { useReportData } from "./useReportData";
import { useReportTerms } from "./useReportTerms";

export const ReportArea = () => {
  const { formatMessage } = useIntl();
  const [maximizeModalOpen, setMaximizeModalOpen] = useState(false);
  const [reportToRegenerate, setReportToRegenerate] =
    useState<TemplateReportItem | null>(null);
  const { organizationId, caseId, chatId } = useChatDocumentParams();

  // Get case info for PDF exports
  const { project } = useParentCase();
  const caseInfo = project ? formatCaseInfo(project) : undefined;

  // Get organization for Output Language setting
  const { data: organization } = useGetOrganization(organizationId);

  const terms = useReportTerms(caseId);

  const {
    content,
    reportSections,
    templateReports,
    handleGenerateReport,
    isLoading,
    selectedReportIndex,
    setSelectedReportIndex,
    chatTitle,
    handleRegenerateSection,
    handleEditSection,
    locallyGenerating,
  } = useReportData(organizationId, caseId, chatId);

  const selectedTemplate =
    templateReports.length > selectedReportIndex
      ? templateReports[selectedReportIndex].template
      : null;
  const selectedReport =
    templateReports.length > selectedReportIndex
      ? templateReports[selectedReportIndex].report
      : null;

  useEffect(() => {
    if (
      !isLoading &&
      selectedReportIndex === 0 &&
      selectedReport === null &&
      selectedTemplate
    ) {
      handleGenerateReport(selectedTemplate.id);
    }
  }, [
    isLoading,
    selectedReportIndex,
    selectedReport,
    selectedTemplate,
    handleGenerateReport,
  ]);

  const isLocallyGeneratingThisTemplate =
    selectedTemplate != null && locallyGenerating.has(selectedTemplate.id);

  const isGenerateView =
    !isLoading &&
    selectedReportIndex !== 0 &&
    selectedTemplate &&
    !selectedReport &&
    !isLocallyGeneratingThisTemplate;
  const isGenerating = selectedReport !== null;

  const exportFileName = `${selectedTemplate ? selectedTemplate.name : selectedReport?.title} ${chatTitle ? getFileTitle(chatTitle) : ""}`;

  const fullContent = useMemo(() => {
    if (reportSections && reportSections.length > 0) {
      return reportSections
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        .map((section) => `## ${section.title}\n\n${section.content ?? ""}`)
        .join("\n\n");
    }
    return content;
  }, [reportSections, content]);

  const highlightedContent = useMemo(() => {
    if (!fullContent) return "";
    const preprocessed = unwrapAllCodeBlocks(fullContent);
    return highlightTermsInMarkdown(preprocessed, terms);
  }, [fullContent, terms]);

  const templateReportItems: TemplateReportItem[] = templateReports.map(
    (templateReport, index) => {
      const { template, report } = templateReport;

      const id = template?.id ?? index.toString();
      const name = template?.name ?? report?.title ?? "";

      let status: TemplateReportStatus = "failed";
      let isOutdated = false;
      let outdatedReason: OutdatedReason | undefined = undefined;

      if (template) {
        status = "notStarted";
        if (report) {
          switch (report.status) {
            case "completed":
              status = "completed";
              break;
            case "reportProcessing":
              status = "inProgress";
              break;
            case "caseProcessing":
              if (locallyGenerating.has(template.id)) {
                status = "inProgress";
              } else {
                status = "pending";
              }
              break;
            case "outdatedByDoclist":
              status = "completed";
              isOutdated = true;
              outdatedReason = "outdatedByDoclist";
              break;
            case "outdatedByTemplate":
              status = "completed";
              isOutdated = true;
              outdatedReason = "outdatedByTemplate";
              break;
            case "failed":
              status = "failed";
              break;
            default:
              status = "failed";
              break;
          }
        }
        if (locallyGenerating.has(template.id)) {
          status = "inProgress";
        }
      } else if (report) {
        // Report exists but template is deleted
        status = "templateDeleted";
      }

      return {
        id,
        name,
        status,
        isOutdated,
        outdatedReason,
      };
    }
  );

  // Check if any report has caseProcessing status (means case is being reprocessed)
  const isCaseProcessing = templateReports.some(
    (tr) => tr.report?.status === "caseProcessing"
  );

  const inProgress =
    (templateReportItems.length > selectedReportIndex &&
      templateReportItems[selectedReportIndex].status === "inProgress") ||
    isLocallyGeneratingThisTemplate;
  const isPending =
    templateReportItems.length > selectedReportIndex &&
    templateReportItems[selectedReportIndex].status === "pending";

  const showActions = !isGenerateView && !inProgress && !isPending;

  const reportViewStatus: ReportViewStatus = useMemo(() => {
    if (inProgress) {
      return { type: "loading" };
    }
    if (isPending) {
      return { type: "pending" };
    }
    if (
      templateReportItems.length > selectedReportIndex &&
      templateReportItems[selectedReportIndex].status === "templateDeleted"
    ) {
      return { type: "templateDeleted" };
    }
    if (
      templateReportItems.length > selectedReportIndex &&
      templateReportItems[selectedReportIndex].isOutdated
    ) {
      return {
        type: "outdated",
        reason: templateReportItems[selectedReportIndex].outdatedReason,
      };
    }
    return { type: "ready" };
  }, [inProgress, isPending, templateReportItems, selectedReportIndex]);

  const handleConfirmRegenerate = () => {
    if (!reportToRegenerate) return;
    handleGenerateReport(reportToRegenerate.id);
    setReportToRegenerate(null);
  };

  return (
    <>
      <div className="relative">
        <div className="p-3 border-zinc-100 h-[calc(100vh-92px)] overflow-y-auto overflow-x-hidden">
          <TemplateReportSelector
            templateReportItems={templateReportItems}
            selectedTemplateReportIndex={selectedReportIndex}
            onValueChange={setSelectedReportIndex}
            onRegenerateClick={setReportToRegenerate}
          />

          {isGenerateView ? (
            <div className="px-3 flex items-center justify-center h-full">
              <GenerateReportView
                template={selectedTemplate}
                onGenerate={handleGenerateReport}
                isGenerating={isGenerating}
                isPending={isCaseProcessing}
              />
            </div>
          ) : (
            <ReportView
              reportSections={reportSections ?? []}
              status={reportViewStatus}
              terms={terms}
              content={content}
              onRegenerateSection={handleRegenerateSection}
              onEditSection={handleEditSection}
              onRegenerateReport={
                selectedTemplate
                  ? () => handleGenerateReport(selectedTemplate.id)
                  : undefined
              }
              isRegeneratingReport={inProgress}
              templateName={selectedTemplate?.name}
              outputLanguage={organization?.language}
            />
          )}
        </div>
        {showActions && (
          <ReportActions
            disabled={false}
            content={fullContent}
            exportFileName={exportFileName}
            reportTitle={
              selectedTemplate ? selectedTemplate.name : selectedReport?.title
            }
            caseInfo={caseInfo}
            onMaximize={() => {
              trackEvent({ eventName: "app/doc_list/maximize.button/click" });
              setMaximizeModalOpen(true);
            }}
          />
        )}
      </div>
      {maximizeModalOpen && (
        <MarkdownContentModal
          title={formatMessage({ id: `chat.report.title` })}
          filename={exportFileName}
          content={highlightedContent}
          open={maximizeModalOpen}
          onOpenChange={setMaximizeModalOpen}
          copyEventName={`app/report_maximize_modal/copy_all.button/click`}
          exportOpenEventName={`app/report__maximize_modal/export.button/click`}
          exportEventName={`app/report__maximize_modal/export_menu_open/export_action.link/click`}
          terms={terms}
          caseInfo={caseInfo}
        />
      )}
      <RegenerateReportModal
        isOpen={reportToRegenerate !== null}
        onClose={() => setReportToRegenerate(null)}
        onConfirm={handleConfirmRegenerate}
        reportName={
          reportToRegenerate?.name ??
          (templateReportItems.length > selectedReportIndex
            ? templateReportItems[selectedReportIndex].name
            : "")
        }
      />
    </>
  );
};
